import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { ProgressBar } from 'primereact/progressbar';
import { useAuthContext } from './context/AuthContext';
import type { DecryptedUser } from './shared/types/appTypes';
import { canViewScope, cantViewScope } from './services/ScopeService';

// ---------------------------------------------------------------------------
// Lazy page imports — each page is split into its own chunk
// ---------------------------------------------------------------------------

const Case = lazy(() => import('./pages/Case'));
const CasesPage = lazy(() => import('./pages/Cases'));
const Login = lazy(() => import('./pages/Login'));
const SSOLogin = lazy(() => import('./pages/SSOLogin'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const EmailSettings = lazy(() => import('./pages/EmailSettings'));
const Monitors = lazy(() => import('./pages/Monitors'));
const MonitorDetail = lazy(() => import('./pages/MonitorDetail'));
const Queries = lazy(() => import('./pages/Queries'));
const Query = lazy(() => import('./pages/Query'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Admin pages
const AdminGroups = lazy(() => import('./pages/admin/Groups'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminUsersAdd = lazy(() => import('./pages/admin/UsersAdd'));
const AdminAgencies = lazy(() => import('./pages/admin/Agencies'));
const AdminAgency = lazy(() => import('./pages/admin/Agency'));
const AdminAgencyPrompts = lazy(() => import('./pages/admin/AgencyPrompts'));
const AdminAgencyPromptEdit = lazy(
  () => import('./pages/admin/AgencyPromptEdit'),
);
const AdminAgencyDomains = lazy(() => import('./pages/admin/AgencyDomains'));
const AdminAgencyDomainsAdd = lazy(
  () => import('./pages/admin/AgencyDomainsAdd'),
);
const AdminQueryGroups = lazy(() => import('./pages/admin/QueryGroups'));
const AdminQueries = lazy(() => import('./pages/admin/Queries'));
const AdminAssignments = lazy(() => import('./pages/admin/Assignments'));
const AdminAssignmentsAdd = lazy(() => import('./pages/admin/AssignmentsAdd'));
const UsageReporting = lazy(
  () => import('./pages/admin/usageReporting/UsageReporting'),
);

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

interface AppRouterProps {
  handleAuthentication: (
    authenticated: boolean,
    userRecord: DecryptedUser | string,
  ) => Promise<void>;
  homePage: string;
  user: { email: string } | null;
}

const AppRouter = ({
  handleAuthentication,
  homePage,
  user,
}: AppRouterProps) => {
  const { profile, impersonation, factors } = useAuthContext();

  return (
    <Suspense fallback={<ProgressBar mode='indeterminate' />}>
      <Routes>
        <Route path='/' element={<Navigate to={homePage} replace />} />
        <Route
          path='/login'
          element={<Login handleAuthentication={handleAuthentication} />}
        />
        <Route
          path='/ssologin'
          element={<SSOLogin handleAuthentication={handleAuthentication} />}
        />
        <Route path='/forgotpassword' element={<ForgotPassword />} />
        <Route
          path='/changepassword'
          element={<ChangePassword user={user ?? { email: '' }} />}
        />
        <Route
          path='/emailsettings'
          element={
            <EmailSettings
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route
          path='/cases/:caseID'
          element={
            <Case
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
              factors={factors}
            />
          }
        />
        <Route
          path='/cases'
          element={
            <CasesPage
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
              factors={factors}
            />
          }
        />
        <Route
          path='/monitors'
          element={
            canViewScope(
              'monitors_enabled_in_elevate',
              profile,
              impersonation,
            ) ? (
              <Monitors />
            ) : (
              <Navigate to={homePage} replace />
            )
          }
        />
        <Route
          path='/monitors/:monitorId'
          element={
            canViewScope(
              'monitors_enabled_in_elevate',
              profile,
              impersonation,
            ) ? (
              <MonitorDetail />
            ) : (
              <Navigate to={homePage} replace />
            )
          }
        />
        <Route
          path='/queries/:queryID'
          element={
            cantViewScope(
              'queries_disabled_in_elevate',
              profile,
              impersonation,
            ) ? (
              <Navigate to={homePage} replace />
            ) : (
              <Query
                profile={profile as NonNullable<typeof profile>}
                impersonation={impersonation}
                factors={factors}
              />
            )
          }
        />
        <Route
          path='/queries'
          element={
            cantViewScope(
              'queries_disabled_in_elevate',
              profile,
              impersonation,
            ) ? (
              <Navigate to={homePage} replace />
            ) : (
              <Queries
                profile={profile as NonNullable<typeof profile>}
                impersonation={impersonation}
              />
            )
          }
        />
        <Route
          path='/admin/users/add'
          element={
            <AdminUsersAdd
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route
          path='/admin/users'
          element={
            <AdminUsers
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route
          path='/admin/groups'
          element={
            <AdminGroups
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route
          path='/admin/agencies/:agencyID/domains/add'
          element={
            <AdminAgencyDomainsAdd
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route
          path='/admin/agencies/:agencyID/domains'
          element={
            <AdminAgencyDomains
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route
          path='/admin/agencies/:agencyID/prompts/:promptID'
          element={
            <AdminAgencyPromptEdit
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route
          path='/admin/agencies/:agencyID/prompts'
          element={
            <AdminAgencyPrompts
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route
          path='/admin/agencies/:agencyID'
          element={
            <AdminAgency
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route
          path='/admin/agencies'
          element={
            <AdminAgencies
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route
          path='/admin/queryGroups'
          element={
            <AdminQueryGroups
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route
          path='/admin/queries'
          element={
            <AdminQueries
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route
          path='/admin/assignments/add'
          element={
            <AdminAssignmentsAdd
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route
          path='/admin/assignments/:userID'
          element={
            <AdminAssignments
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route
          path='/admin/assignments'
          element={
            <AdminAssignments
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route path='/admin/usageReporting' element={<UsageReporting />} />
        <Route
          path='/admin'
          element={
            <AdminUsers
              profile={profile as NonNullable<typeof profile>}
              impersonation={impersonation}
            />
          }
        />
        <Route path='/contact' element={<ContactPage />} />
        <Route path='/NotFound' element={<NotFound />} />
        <Route path='*' element={<Navigate to='/NotFound' replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
